/**
 * Registro de "shapes" dos parâmetros (camada 1) — a UI NUNCA expõe o payload
 * JSON: cada chave tem campos nomeados em pt-BR (moeda/percentual/inteiro/
 * hh:mm/booleano/seleção/grupo) com ajuda curta, e a tabela de faixas (INSS/
 * IRRF) vira editor de linhas com colunas nomeadas. O JSON continua existindo
 * na API — o formulário o MONTA a partir dos campos.
 *
 * Unidades na borda: moeda em centavos, alíquotas em ppm (11,00% = 110000),
 * tempos em minutos.
 */
import { fmtMoeda, fmtHhMm } from "./format";

export type CampoSimples =
  | { kind: "moeda"; nome: string; rotulo: string; ajuda?: string }
  | { kind: "percentual"; nome: string; rotulo: string; ajuda?: string }
  | { kind: "inteiro"; nome: string; rotulo: string; ajuda?: string }
  | { kind: "hhmm"; nome: string; rotulo: string; ajuda?: string }
  | { kind: "booleano"; nome: string; rotulo: string; ajuda?: string }
  | { kind: "selecao"; nome: string; rotulo: string; opcoes: { valor: string; rotulo: string }[]; ajuda?: string };

export type CampoShape = CampoSimples | { kind: "grupo"; nome: string; rotulo: string; ajuda?: string; campos: CampoSimples[] };

/** Coluna do editor de faixas. */
export type ColunaFaixa = "de" | "ate" | "aliquota" | "parcela";

export interface ShapeParametro {
  chave: string;
  rotulo: string;
  descricao: string;
  campos: CampoShape[];
  faixas?: { rotulo: string; colunas: ColunaFaixa[]; ajuda?: string };
}

export const ROTULO_COLUNA_FAIXA: Record<ColunaFaixa, string> = {
  de: "De (R$)",
  ate: "Até (R$)",
  aliquota: "Alíquota (%)",
  parcela: "Parcela a deduzir (R$)",
};

export const SHAPES_PARAMETROS: ShapeParametro[] = [
  {
    chave: "inss",
    rotulo: "INSS (tabela progressiva)",
    descricao: "Tabela do ano + teto de base. Fonte: Portaria Interministerial anual; truncado por faixa.",
    campos: [{ kind: "moeda", nome: "tetoBaseCentavos", rotulo: "Teto de base", ajuda: "Acima deste valor vira 'excedente INSS' (8.475,55 em 2026)." }],
    faixas: { rotulo: "Faixas do INSS", colunas: ["de", "ate", "aliquota"], ajuda: "Contíguas e sem buracos: o 'de' de uma linha é o 'até' da anterior + 0,01." },
  },
  {
    chave: "irrf",
    rotulo: "IRRF (tabela + LC 15.270/2025)",
    descricao: "Tabela progressiva (base × alíquota − parcela), deduções e o bloco da Lei Complementar 15.270/2025.",
    campos: [
      { kind: "moeda", nome: "deducaoDependenteCentavos", rotulo: "Dedução por dependente", ajuda: "Congelada em 189,59 desde 2015." },
      { kind: "moeda", nome: "descontoSimplificadoCentavos", rotulo: "Desconto simplificado", ajuda: "Substitui TODAS as deduções quando melhor (607,20 em 2026)." },
      { kind: "moeda", nome: "minimoRetencaoCentavos", rotulo: "Mínimo de retenção", ajuda: "IR abaixo disso não é retido (IN RFB 1.500/2014 — 10,00)." },
      {
        kind: "grupo",
        nome: "lc15270",
        rotulo: "LC 15.270/2025 (isenção + redutor)",
        ajuda: "Isenção até o limite; redutor decrescente na faixa seguinte.",
        campos: [
          { kind: "booleano", nome: "ativo", rotulo: "Regra ativa", ajuda: "Liga/desliga a LC 15.270." },
          { kind: "moeda", nome: "limiteIsencaoCentavos", rotulo: "Isento até", ajuda: "5.000,00 em 2026." },
          { kind: "moeda", nome: "redutorInicioCentavos", rotulo: "Redutor a partir de", ajuda: "5.000,01." },
          { kind: "moeda", nome: "redutorFimCentavos", rotulo: "Redutor até", ajuda: "7.350,01." },
          { kind: "moeda", nome: "redutorConstanteCentavos", rotulo: "Constante do redutor", ajuda: "978,62 — fórmula: constante − fator × base." },
          { kind: "percentual", nome: "redutorFatorPpm", rotulo: "Fator do redutor", ajuda: "13,3145% (0,133145) em 2026." },
          {
            kind: "selecao",
            nome: "baseReferencia",
            rotulo: "Base de referência dos testes",
            opcoes: [
              { valor: "bruto_tributavel", rotulo: "Bruto tributável (planilha/Domínio em JAN–JUN/26)" },
              { valor: "base_ir", rotulo: "Base de IR (oficial — desde 07/2026)" },
            ],
            ajuda: "Muda o número testado na isenção/redutor (decisão do dono, FILTRAGEM §3.5).",
          },
        ],
      },
    ],
    faixas: { rotulo: "Faixas do IRRF", colunas: ["de", "aliquota", "parcela"], ajuda: "A primeira linha começa em 0,00 (isento); 'de' sempre crescente." },
  },
  {
    chave: "fgts",
    rotulo: "FGTS",
    descricao: "Depósito mensal (8% truncado por pessoa).",
    campos: [{ kind: "percentual", nome: "aliquotaPpm", rotulo: "Alíquota", ajuda: "8,00%." }],
  },
  {
    chave: "patronal",
    rotulo: "Encargos patronais (CLT)",
    descricao: "Empresa 20% + terceiros 5,8% + RAT×FAP (divergência RAT cadastrado × efetivo documentada).",
    campos: [
      { kind: "percentual", nome: "empresaPpm", rotulo: "Empresa", ajuda: "20,00% (Lei 8.212/91)." },
      { kind: "percentual", nome: "terceirosPpm", rotulo: "Terceiros", ajuda: "5,80%." },
      { kind: "percentual", nome: "ratFapPpm", rotulo: "RAT × FAP", ajuda: "2026 oficial: RAT 2,0% × FAP 1,8274 = 3,6548% (doc oficial 30/09/2025)." },
    ],
  },
  {
    chave: "vt",
    rotulo: "Vale-transporte",
    descricao: "Teto legal do desconto: 6% do salário do mês (Lei 7.418/85).",
    campos: [{ kind: "percentual", nome: "percentualMaximoPpm", rotulo: "Desconto máximo", ajuda: "6,00% — desconto = min(este %, custo)." }],
  },
  {
    chave: "vr",
    rotulo: "Vale-refeição",
    descricao: "Valor do VR dia (dissídio 31/03/2026: 36,00 → 38,00).",
    campos: [{ kind: "moeda", nome: "vrDiarioCentavos", rotulo: "VR por dia", ajuda: "Creditado no mês seguinte, sem desconto do funcionário." }],
  },
  {
    chave: "contribuicao_confederativa",
    rotulo: "Contribuição confederativa",
    descricao: "Evento 260 — 10,00 → 13,00 em 06/2026 (fundamento pendente, dúvida 52).",
    campos: [{ kind: "moeda", nome: "valorCentavos", rotulo: "Valor mensal", ajuda: "Desconto fixo por funcionário ativo." }],
  },
  {
    chave: "caju_mais",
    rotulo: "Caju+ (Gympass/Conexa)",
    descricao: "Custo empresa 25,00/mês + desconto simbólico 1,00.",
    campos: [{ kind: "moeda", nome: "valorCentavos", rotulo: "Custo mensal (empresa)", ajuda: "Fora do líquido." }],
  },
  {
    chave: "auxilio_home_office",
    rotulo: "Auxílio home office",
    descricao: "Custo empresa 250,00/mês + desconto simbólico 1,00 (evento 220).",
    campos: [{ kind: "moeda", nome: "valorCentavos", rotulo: "Custo mensal (empresa)", ajuda: "Integra o líquido com benefícios (BN)." }],
  },
  {
    chave: "seguro_vida",
    rotulo: "Seguro de vida",
    descricao: "Custo empresa 27,78/mês + desconto simbólico 1,00 (evento 290).",
    campos: [{ kind: "moeda", nome: "valorCentavos", rotulo: "Custo mensal (empresa)", ajuda: "Israel não tem (cadastro individual)." }],
  },
  {
    chave: "desconto_simbolico",
    rotulo: "Descontos simbólicos",
    descricao: "R$ 1,00 dos eventos 220/290/344 (dúvida 1 do dono — parametrizado por rubrica).",
    campos: [{ kind: "moeda", nome: "valorCentavos", rotulo: "Valor simbólico", ajuda: "Aplicado em VR/seguro/home office." }],
  },
  {
    chave: "he",
    rotulo: "Horas extras e adicional noturno",
    descricao: "Fatores de HE (piso CF 50%; dobro em feriado) e adicional noturno 20% (hora 52:30).",
    campos: [
      { kind: "percentual", nome: "heFator50Ppm", rotulo: "Fator HE 50%", ajuda: "150% = 1,5× o valor-hora." },
      { kind: "percentual", nome: "heFator100Ppm", rotulo: "Fator HE 100%", ajuda: "200% = 2,0× (domingo/feriado reclassificado)." },
      { kind: "percentual", nome: "adicionalNoturnoPpm", rotulo: "Adicional noturno", ajuda: "20% sobre o valor-hora (CLT art. 73)." },
    ],
  },
  {
    chave: "ferias",
    rotulo: "Férias",
    descricao: "Incidências do 1/3 constitucional (STF: sem INSS — Tema 985; com FGTS por default).",
    campos: [{ kind: "booleano", nome: "fgtsIncideTerco", rotulo: "FGTS sobre o 1/3 de férias", ajuda: "Ligado em 2026." }],
  },
  {
    chave: "banco_horas",
    rotulo: "Banco de horas",
    descricao: "Cap diário (evidência 2h) e prazo de compensação (dúvida aberta do dono — defaults documentados). Saldo em desligamento: M4.2 — pago conforme CLT art. 59 §3º a partir de 08/2026. M3.2: destino do residual no fim do ciclo e tolerâncias dos alertas.",
    campos: [
      { kind: "hhmm", nome: "capDiarioMinutos", rotulo: "Cap diário no banco", ajuda: "2:00 — o excedente vira HE paga (Pedro 17/06: 2h00 banco + 0h45 paga)." },
      { kind: "inteiro", nome: "prazoCompensacaoMeses", rotulo: "Prazo de compensação (meses)", ajuda: "Default 6 — DÚVIDA ABERTA do dono." },
      { kind: "booleano", nome: "pagaSaldoDesligamento", rotulo: "Paga saldo positivo em desligamento", ajuda: "Default SIM — CLT art. 59 §3º (M4.2, a partir de 08/2026): relatório de saldo no desligamento; a baixa é na rescisão (Contmega — D15/M5.5)." },
      {
        kind: "selecao",
        nome: "destinoResidualCiclo",
        rotulo: "Destino do residual no fim do ciclo",
        opcoes: [
          { valor: "observar", rotulo: "Observar (default — só registra/alerta)" },
          { valor: "transfere_previous_balance", rotulo: "Transfere como saldo anterior do ciclo novo" },
          { valor: "zera", rotulo: "Zera (ciclo novo abre em 0:00)" },
        ],
        ajuda: "DÚVIDA D7 ABERTA — define o previous_balance ESPERADO na abertura do ciclo seguinte; divergência vira alerta (tolerância 0). Nunca apura/paga o residual automaticamente.",
      },
      { kind: "hhmm", nome: "toleranciaSaldoNegativoMinutos", rotulo: "Tolerância de saldo negativo residual", ajuda: "Default 0:05 — caso real Daniel −0:01 (07/2026). Acima: alerta de revisão (nunca ajuste automático)." },
      { kind: "hhmm", nome: "alertaFimCicloSaldoMinutos", rotulo: "Saldo mínimo p/ alerta no penúltimo mês", ajuda: "Default 2:00 — no último mês do ciclo o alerta é sempre; no penúltimo só com saldo alto." },
    ],
  },
  {
    chave: "inss_contribuinte_individual",
    rotulo: "INSS do contribuinte individual (sócios)",
    descricao: "Alíquota × min(pró-labore, teto). 11% = prática da contabilidade (golden); 20% = regra legal (Lei 8.212 art. 21) — enquadramento pendente com o contador.",
    campos: [
      { kind: "percentual", nome: "aliquotaPpm", rotulo: "Alíquota", ajuda: "11,00% vigente (prática) — divergência documentada × 20% legal." },
      { kind: "moeda", nome: "tetoBaseCentavos", rotulo: "Teto de base", ajuda: "O mesmo teto do INSS CLT (8.475,55 em 2026)." },
    ],
  },
  {
    chave: "patronal_contribuinte",
    rotulo: "Patronal sobre contribuintes (sócios)",
    descricao: "20% × min(pró-labore, teto) — CC8 do golden (Lei 8.212 art. 22, III).",
    campos: [{ kind: "percentual", nome: "aliquotaPpm", rotulo: "Alíquota", ajuda: "20,00%." }],
  },
];

export function shapeDaChave(chave: string): ShapeParametro | undefined {
  return SHAPES_PARAMETROS.find((s) => s.chave === chave);
}

// ---------------------------------------------------------------------------
// Valores iniciais × montagem × exibição
// ---------------------------------------------------------------------------

function valorInicialCampo(c: CampoSimples): unknown {
  switch (c.kind) {
    case "booleano":
      return true;
    case "selecao":
      return c.opcoes[0]!.valor;
    default:
      return null;
  }
}

/** Payload inicial do formulário (campos vazios; booleanos/seleções com default). */
export function payloadInicial(shape: ShapeParametro): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  for (const c of shape.campos) {
    if (c.kind === "grupo") {
      p[c.nome] = Object.fromEntries(c.campos.map((g) => [g.nome, valorInicialCampo(g)]));
    } else {
      p[c.nome] = valorInicialCampo(c);
    }
  }
  return p;
}

/** Validação dos campos do payload (mensagens legíveis pt-BR). */
export function validarPayload(shape: ShapeParametro, payload: Record<string, unknown>): string[] {
  const problemas: string[] = [];
  const checar = (c: CampoSimples, v: unknown, prefixo: string) => {
    if (c.kind === "booleano" || c.kind === "selecao") return;
    if (v === null || v === undefined) {
      problemas.push(`${prefixo}${c.rotulo}: preencha o valor`);
      return;
    }
    if (typeof v !== "number" || Number.isNaN(v)) {
      problemas.push(`${prefixo}${c.rotulo}: valor inválido`);
      return;
    }
    if (c.kind === "percentual" && (v < 0 || v > 1000000)) problemas.push(`${prefixo}${c.rotulo}: informe entre 0,00% e 100,00%`);
    if ((c.kind === "moeda" || c.kind === "inteiro") && v < 0) problemas.push(`${prefixo}${c.rotulo}: não pode ser negativo`);
    if (c.kind === "hhmm" && v < 0) problemas.push(`${prefixo}${c.rotulo}: não pode ser negativo`);
  };
  for (const c of shape.campos) {
    if (c.kind === "grupo") {
      const g = (payload[c.nome] ?? {}) as Record<string, unknown>;
      for (const gc of c.campos) checar(gc, g[gc.nome], `${c.rotulo} → `);
    } else {
      checar(c, payload[c.nome], "");
    }
  }
  return problemas;
}

export interface FaixaEdicao {
  deCentavos: number | null;
  ateCentavos: number | null;
  aliquotaPpm: number | null;
  parcelaCentavos: number | null;
}

/** Faixas da API → linhas do editor. */
export function faixasParaEdicao(
  faixas: { limiteInferiorCentavos: number; limiteSuperiorCentavos: number | null; aliquotaPpm: number; parcelaDeduzirCentavos: number | null }[],
): FaixaEdicao[] {
  return faixas.map((f) => ({
    deCentavos: f.limiteInferiorCentavos,
    ateCentavos: f.limiteSuperiorCentavos,
    aliquotaPpm: f.aliquotaPpm,
    parcelaCentavos: f.parcelaDeduzirCentavos,
  }));
}

/** Linhas do editor → faixas da API (ordem pela coluna "de"). */
export function edicaoParaFaixas(linhas: FaixaEdicao[]): {
  limiteInferiorCentavos: number;
  limiteSuperiorCentavos?: number;
  aliquotaPpm: number;
  parcelaDeduzirCentavos?: number;
}[] {
  return linhas.map((l) => ({
    limiteInferiorCentavos: l.deCentavos!,
    ...(l.ateCentavos !== null ? { limiteSuperiorCentavos: l.ateCentavos } : {}),
    aliquotaPpm: l.aliquotaPpm!,
    ...(l.parcelaCentavos !== null ? { parcelaDeduzirCentavos: l.parcelaCentavos } : {}),
  }));
}

/** Validação da tabela de faixas (contiguidade/cobertura — mensagem legível). */
export function validarFaixas(shape: ShapeParametro, linhas: FaixaEdicao[]): string[] {
  const problemas: string[] = [];
  if (!shape.faixas) return problemas;
  const colunas = shape.faixas.colunas;
  if (linhas.length === 0) {
    problemas.push(`${shape.faixas.rotulo}: inclua ao menos uma linha`);
    return problemas;
  }
  linhas.forEach((l, i) => {
    const n = i + 1;
    if (l.deCentavos === null) problemas.push(`Linha ${n}: preencha "De"`);
    if (colunas.includes("ate") && l.ateCentavos === null && i < linhas.length - 1) problemas.push(`Linha ${n}: preencha "Até" (só a última pode ficar sem teto)`);
    if (l.aliquotaPpm === null) problemas.push(`Linha ${n}: preencha a alíquota`);
    if (colunas.includes("parcela") && l.parcelaCentavos === null) problemas.push(`Linha ${n}: preencha a parcela a deduzir`);
    if (l.deCentavos !== null && l.ateCentavos !== null && l.ateCentavos < l.deCentavos) {
      problemas.push(`Linha ${n}: "Até" (${fmtMoeda(l.ateCentavos)}) menor que "De" (${fmtMoeda(l.deCentavos)})`);
    }
    if (l.aliquotaPpm !== null && (l.aliquotaPpm < 0 || l.aliquotaPpm > 1000000)) problemas.push(`Linha ${n}: alíquota fora de 0–100%`);
  });
  if (problemas.length > 0) return problemas;
  // cobertura: primeira linha começa em 0
  if (linhas[0]!.deCentavos !== 0) {
    problemas.push(`A primeira faixa deve começar em 0,00 (hoje começa em ${fmtMoeda(linhas[0]!.deCentavos)})`);
  }
  // ordenação/contiguidade
  for (let i = 1; i < linhas.length; i++) {
    const ant = linhas[i - 1]!;
    const atual = linhas[i]!;
    if (colunas.includes("ate")) {
      if (ant.ateCentavos !== null && atual.deCentavos! !== ant.ateCentavos + 1) {
        problemas.push(
          `Buraco ou sobreposição entre as linhas ${i} e ${i + 1}: a linha ${i} termina em ${fmtMoeda(ant.ateCentavos)} ` +
            `e a ${i + 1} começa em ${fmtMoeda(atual.deCentavos)} — o "De" correto seria ${fmtMoeda(ant.ateCentavos + 1)}`,
        );
      }
    } else if (atual.deCentavos! <= ant.deCentavos!) {
      problemas.push(`Linha ${i + 1}: "De" deve ser maior que o da linha ${i} (tabela progressiva crescente)`);
    }
  }
  return problemas;
}

// ---------------------------------------------------------------------------
// Exibição legível das vigências existentes (sem JSON)
// ---------------------------------------------------------------------------

function formatarCampo(c: CampoSimples, v: unknown): string {
  if (v === null || v === undefined) return "—";
  switch (c.kind) {
    case "moeda":
      return fmtMoeda(v as number);
    case "percentual":
      return `${((v as number) / 10000).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`;
    case "hhmm":
      return fmtHhMm(v as number);
    case "booleano":
      return v ? "sim" : "não";
    case "selecao":
      return c.opcoes.find((o) => o.valor === v)?.rotulo ?? String(v);
    default:
      return String(v);
  }
}

/** Linhas [rótulo, valor] formatadas do payload de uma vigência (para a tabela). */
export function formatarPayload(shape: ShapeParametro, payload: Record<string, unknown>): { rotulo: string; valor: string }[] {
  const linhas: { rotulo: string; valor: string }[] = [];
  for (const c of shape.campos) {
    if (c.kind === "grupo") {
      const g = (payload[c.nome] ?? {}) as Record<string, unknown>;
      for (const gc of c.campos) {
        linhas.push({ rotulo: `${c.rotulo} → ${gc.rotulo}`, valor: formatarCampo(gc, g[gc.nome]) });
      }
    } else {
      linhas.push({ rotulo: c.rotulo, valor: formatarCampo(c, payload[c.nome]) });
    }
  }
  return linhas;
}
